import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  User, 
  EnvelopeSimple, 
  Lock, 
  UserCircle,
  FirstAidKit,
  CaretRight
} from '@phosphor-icons/react';

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'patient', // Always patient for public registration
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        navigateByRole(user.role);
      } else {
        const user = await register(formData);
        navigateByRole(user.role);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const navigateByRole = (role) => {
    let path;
    if (role === 'patient') path = '/patient/appointments';
    else if (role === 'cma') path = '/staff/schedule';
    else path = `/${role}`;
    // Use window.location to ensure full page reload with auth state
    window.location.href = path;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/5452188/pexels-photo-5452188.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Healthcare professional"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
            T-MEDCMA
          </h2>
          <p className="text-slate-300 text-lg leading-relaxed">
            AI-powered healthcare platform that streamlines patient care, reduces provider burnout, 
            and closes care gaps through intelligent workflows.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="flex items-center gap-2 text-teal-600 mb-8">
              <FirstAidKit size={32} weight="duotone" />
              <span className="font-bold text-xl" style={{ fontFamily: 'Manrope' }}>T-MEDCMA</span>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              {isLogin ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="text-slate-500 mt-2">
              {isLogin ? 'Sign in to access your dashboard' : 'Get started with T-MEDCMA'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label-clinical">First Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="text"
                        required
                        data-testid="register-first-name"
                        className="input-clinical pl-10"
                        placeholder="John"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label-clinical">Last Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="text"
                        required
                        data-testid="register-last-name"
                        className="input-clinical pl-10"
                        placeholder="Doe"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Note: Staff accounts are created by administrators */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <UserCircle size={16} className="inline mr-1" />
                    Registering as a <strong>Patient</strong>. Staff accounts are created by clinic administrators.
                  </p>
                </div>
              </>
            )}

            <div>
              <label className="label-clinical">Email</label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="email"
                  required
                  data-testid="auth-email"
                  className="input-clinical pl-10"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="label-clinical">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="password"
                  required
                  data-testid="auth-password"
                  className="input-clinical pl-10"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="w-full btn-teal py-3 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="spinner" />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <CaretRight size={18} weight="bold" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-teal-600 hover:text-teal-700 font-medium text-sm"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
